/**
 * CompetitorEVChart — EV/EBITDA multiple per year (bar chart).
 *
 * In a project context this would show company vs competitors;
 * currently shows the current company's trend across years.
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
  Cell,
} from 'recharts';
import ChartCard, { FocalBadge, YearBadge, focalTickFactory, yearTickFactory } from './ChartCard';
import ChartTooltip from './ChartTooltip';
import { tooltipNotesForYear, isEstimatedValue, ESTIMATED_FILL_OPACITY } from './chartNotes';
import MarketDataFootnote from './MarketDataFootnote';
import FinancialNoData from '../FinancialNoData';
import { formatFiscalYear } from '../utils/formatters';
import { PEER_COLORS, PEER_BAR_UNIFORM, BAR_MAX_SIZE, gridProps, yAxisProps, refLineProps, CHART_INK, CHART_TYPE } from './chartTheme';
import type { UnifiedFinancialRecord, RegionConfig, DataNote } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
  /**
   * Optional focal-company name. Required when `competitorData` is provided
   * so the chart can label the focal bar in peer mode.
   */
  companyName?: string;
  /**
   * When provided (non-empty), switches the chart to peer-comparison mode:
   * one bar per company showing the latest EV/EBITDA multiple.
   */
  competitorData?: Record<string, UnifiedFinancialRecord[]>;
}

function latestEvEbitda(records: UnifiedFinancialRecord[]): { value: number; year: number } | null {
  const sorted = [...records].sort((a, b) => b.year - a.year);
  for (const r of sorted) {
    if (r.ev_ebitda != null) return { value: r.ev_ebitda, year: r.year };
  }
  return null;
}

export default function CompetitorEVChart({ data, region, companyName, competitorData }: Props) {
  const isPeerMode = !!competitorData && Object.keys(competitorData).length > 0 && !!companyName;

  if (isPeerMode) {
    const focalEv = latestEvEbitda(data);
    // Insufficient peer data is a feature-not-available case, not a no-data one.
    if (focalEv == null) return null;

    const peerRows = [
      { company: companyName!, evEbitda: parseFloat(focalEv.value.toFixed(2)), isFocal: true },
      ...Object.entries(competitorData!)
        .map(([name, records]) => {
          const ev = latestEvEbitda(records);
          return ev != null ? { company: name, evEbitda: parseFloat(ev.value.toFixed(2)), isFocal: false } : null;
        })
        .filter((r): r is { company: string; evEbitda: number; isFocal: boolean } => r != null),
    ].sort((a, b) => b.evEbitda - a.evEbitda);

    if (peerRows.length < 2) return null;

    const avg = peerRows.reduce((s, r) => s + r.evEbitda, 0) / peerRows.length;

    return (
      <ChartCard
        title="EV/EBITDA Multiple — Peer Comparison (x)"
        subtitle="Latest reported EV/EBITDA per company, sorted by valuation."
        action={
          <div className="flex items-center gap-2">
            <YearBadge label={formatFiscalYear(focalEv.year, region)} />
            <FocalBadge name={companyName!} />
          </div>
        }
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={peerRows} margin={{ top: 24, right: 16, left: 8, bottom: 60 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="company" interval={0} height={70} tick={focalTickFactory(companyName!)} />
            <YAxis {...yAxisProps} tickFormatter={(v) => `${v?.toFixed(1)}x`} domain={['auto', 'auto']} />
            <ReferenceLine
              y={avg}
              {...refLineProps}
              label={{ value: `Avg ${avg.toFixed(1)}x`, position: 'right', fill: CHART_INK.muted, fontSize: CHART_TYPE.axisTick }}
            />
            <Tooltip content={<ChartTooltip format={(v) => `${v.toFixed(2)}x`} />} />
            {/* One uniform tone for every company; the focal is marked by its
                bold ★ axis tick, matching the EV Insights stacked charts. */}
            <Bar dataKey="evEbitda" name="EV/EBITDA" radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
              <LabelList
                dataKey="evEbitda"
                position="top"
                formatter={(v: number) => `${v?.toFixed(1)}x`}
                style={{ fontSize: CHART_TYPE.dataLabel, fill: CHART_INK.label, fontWeight: 600 }}
              />
              {peerRows.map((row, i) => (
                <Cell key={i} fill={PEER_BAR_UNIFORM} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    );
  }

  // Carry data_notes through: the chart maps records down to a flat view-model,
  // so anything not copied here is unreachable from the tick/tooltip renderers.
  // KEEP years with no EV/EBITDA (do NOT filter them out): an empty column stays
  // on the axis with its ⚠️ tick, and hovering it surfaces WHY the year is blank
  // (its own note, or a generic "no data" fallback) instead of silently vanishing.
  const chartData = data.map((d) => {
    const hasValue = d.ev_ebitda != null;
    return {
      year: formatFiscalYear(d.year, region),
      evEbitda: hasValue ? parseFloat(d.ev_ebitda!.toFixed(2)) : null,
      // Estimated years are drawn lighter (the price behind EV/EBITDA was an AI
      // estimate, not observed market data). Matches the ⚠ tick this same year gets.
      estimated: hasValue && isEstimatedValue(d.data_notes, 'ev_ebitda'),
      notes: tooltipNotesForYear(d.data_notes, hasValue),
    };
  });

  const pricedData = chartData.filter((d) => d.evEbitda != null);

  // Rendered under a SectionDivider — a message keeps the section from dangling.
  // Only a genuinely empty series (no year has a value) falls back to the card.
  if (pricedData.length === 0) {
    return <FinancialNoData state="metric-missing" metricLabel="EV / EBITDA" cardTitle="EV/EBITDA Multiple Comparison (x)" />;
  }

  // Average over PRICED years only — a missing year is not a zero.
  const avg = pricedData.reduce((s, d) => s + (d.evEbitda ?? 0), 0) / pricedData.length;
  const notesByYear = new Map<string, DataNote[]>(chartData.map((d) => [d.year, d.notes]));

  return (
    <ChartCard
      title="EV/EBITDA Multiple Comparison (x)"
      subtitle="Historical EV/EBITDA with average reference"
    >
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData} margin={{ top: 24, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="year" tick={yearTickFactory(notesByYear)} />
          <YAxis {...yAxisProps} tickFormatter={(v) => `${v?.toFixed(1)}x`} domain={['auto', 'auto']} />
          <ReferenceLine y={avg} {...refLineProps}
            label={{ value: `Avg ${avg.toFixed(1)}x`, position: 'right', fill: CHART_INK.muted, fontSize: CHART_TYPE.axisTick }}
          />
          <Tooltip content={<ChartTooltip notesKey="notes" format={(v) => `${v.toFixed(2)}x`} />} />
          {/* Single company's historical trend — one series colour, not one per year.
              Empty years remain as categories, so the BarChart's category tooltip and
              the ⚠ tick's native tooltip both explain the gap (no extra hover bar
              needed here — a 2nd bar would group side-by-side and shift the real one). */}
          <Bar dataKey="evEbitda" name="EV/EBITDA" fill={PEER_COLORS[0]} radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
            <LabelList dataKey="evEbitda" position="top" formatter={(v: number) => `${v?.toFixed(1)}x`} style={{ fontSize: CHART_TYPE.dataLabel, fill: CHART_INK.label, fontWeight: 500 }} />
            {chartData.map((d, i) => (
              <Cell key={i} fillOpacity={d.estimated ? ESTIMATED_FILL_OPACITY : 1} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <MarketDataFootnote
        records={data}
        region={region}
        hasMetric={(r) => r.ev_ebitda != null}
        metricLabel="the EV/EBITDA multiple"
      />
    </ChartCard>
  );
}
